import { useState, useEffect } from 'react';
import DigitalTwinRepository from '@/domain/DigitalTwin/DigitalTwinRepository';
import { isValidCoord } from '@/utils/gisUtils';

export interface DTNetworkData {
  nodes: GeoJSON.Feature<GeoJSON.Point>[];
  lines: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>[];
  dataCenter: { longitude: number; latitude: number } | null;
  loading: boolean;
  error: string | null;
}

const repository = new DigitalTwinRepository();

export function useDigitalTwinNetworkData({
  inpId,
  enabled,
}: {
  inpId: number | null | undefined;
  enabled: boolean;
}): DTNetworkData {
  const [nodes, setNodes] = useState<GeoJSON.Feature<GeoJSON.Point>[]>([]);
  const [lines, setLines] = useState<GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>[]>([]);
  const [dataCenter, setDataCenter] = useState<{ longitude: number; latitude: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !inpId) {
      setNodes([]);
      setLines([]);
      setDataCenter(null);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const rawResponse = await repository.getGisFilesByInpId(inpId);
        if (cancelled) return;

        const collections: GeoJSON.FeatureCollection[] = Array.isArray(rawResponse)
          ? rawResponse
          : rawResponse
          ? [rawResponse as unknown as GeoJSON.FeatureCollection]
          : [];

        const allNodes: GeoJSON.Feature<GeoJSON.Point>[] = [];
        const allLines: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>[] = [];

        for (const collection of collections) {
          if (!collection?.features) continue;
          for (const feature of collection.features) {
            if (!feature?.geometry) continue;
            if (feature.geometry.type === 'Point') {
              allNodes.push(feature as GeoJSON.Feature<GeoJSON.Point>);
            } else if (
              feature.geometry.type === 'LineString' ||
              feature.geometry.type === 'MultiLineString'
            ) {
              allLines.push(
                feature as GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>
              );
            }
          }
        }

        let sumLon = 0;
        let sumLat = 0;
        let count = 0;

        for (const node of allNodes) {
          const [lon, lat] = node.geometry.coordinates as [number, number];
          if (isValidCoord(lon, lat)) {
            sumLon += lon;
            sumLat += lat;
            count++;
          }
        }

        // Fallback: use first coordinate of each line when there are no point nodes
        if (count === 0) {
          for (const line of allLines) {
            const coords =
              line.geometry.type === 'LineString'
                ? (line.geometry.coordinates as [number, number][])
                : (line.geometry.coordinates[0] as [number, number][]);
            for (const [lon, lat] of coords) {
              if (isValidCoord(lon, lat)) {
                sumLon += lon;
                sumLat += lat;
                count++;
              }
            }
          }
        }

        const totalCoords = allNodes.length + allLines.reduce((acc, l) => {
          return acc + (l.geometry.type === 'LineString' ? l.geometry.coordinates.length : l.geometry.coordinates[0].length);
        }, 0);

        if (totalCoords > 0 && count === 0) {
          const sample = allNodes[0]?.geometry?.coordinates ?? allLines[0]?.geometry?.coordinates?.[0];
          console.warn(
            `[useDigitalTwinNetworkData] INP ${inpId}: all ${totalCoords} coordinates are outside WGS84 bounds.`,
            `Sample coordinate: [${sample}].`,
            `The /inps/${inpId}/geojson endpoint is returning projected (non-WGS84) coordinates.`,
            `Backend must reproject to EPSG:4326 before serving the geojson.`
          );
        }

        const center =
          count > 0 ? { longitude: sumLon / count, latitude: sumLat / count } : null;

        setNodes(allNodes);
        setLines(allLines);
        setDataCenter(center);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message ?? 'Error fetching DT network data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [inpId, enabled]);

  return { nodes, lines, dataCenter, loading, error };
}

export default useDigitalTwinNetworkData;
